import {
   FilterCriterion,
   FilterFieldDef,
   FilterOperator
} from '../models/report-filtering';
import { ReportRowVm } from '../models/report-row-vm';

function toStr(v: any): string {
   return (v ?? '').toString().trim();
}

function toNumber(v: any): number | null {
   if (v === null || v === undefined || v === '') return null;
   const n = Number(v);
   return Number.isFinite(n) ? n : null;
}

function toDateMs(v: any): number | null {
   if (!v) return null;

   // PrimeNG p-calendar -> Date object (local timezone)
   if (v instanceof Date) {
      return new Date(v.getFullYear(), v.getMonth(), v.getDate()).getTime();
   }

   const s = toStr(v);
   if (!s) return null;

   // If we have an ISO date like "2026-02-02" (or "2026-02-02T...")
   const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
   if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const d = Number(m[3]);
      return new Date(y, mo, d).getTime(); // local midnight
   }

   // fallback: parse anything else, then normalize to local midnight
   const parsed = new Date(s);
   if (Number.isNaN(parsed.getTime())) return null;

   return new Date(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate()
   ).getTime();
}

function getFieldValue(row: ReportRowVm, field: FilterFieldDef): any {
   return field.getValue ? field.getValue(row) : row.cells[field.key];
}

function stringMatch(op: FilterOperator, aRaw: any, bRaw: any): boolean {
   const a = toStr(aRaw).toLowerCase();
   const b = toStr(bRaw).toLowerCase();

   if (op === 'isEmpty') return a.length === 0;
   if (op === 'isNotEmpty') return a.length > 0;

   if (!b) return true; // treat empty filter value as "no-op"

   switch (op) {
      case 'eq':
         return a === b;
      case 'contains':
         return a.includes(b);
      case 'startsWith':
         return a.startsWith(b);
      case 'endsWith':
         return a.endsWith(b);
      default:
         return true;
   }
}

function compare(
   op: FilterOperator,
   a: number | null,
   b: number | null,
   c: number | null = null
): boolean {
   if (op === 'isEmpty') return a === null;
   if (op === 'isNotEmpty') return a !== null;

   if (a === null) return false;

   // empty filter value = no-op (except between)
   if (b === null && op !== 'between') return true;

   switch (op) {
      case 'eq':
         return b === null ? true : a === b;
      case 'neq':
         return b === null ? true : a !== b;
      case 'gt':
         return b === null ? true : a > b;
      case 'gte':
         return b === null ? true : a >= b;
      case 'lt':
         return b === null ? true : a < b;
      case 'lte':
         return b === null ? true : a <= b;
      case 'between': {
         const lo = b;
         const hi = c;
         if (lo === null || hi === null) return true; // incomplete between => no-op
         return a >= lo && a <= hi;
      }
      default:
         return true;
   }
}

function rowMatchesCriterion(
   row: ReportRowVm,
   field: FilterFieldDef,
   criterion: FilterCriterion
): boolean {
   if (!criterion.operator) return true;

   const raw = getFieldValue(row, field);

   if (field.type === 'string') {
      return stringMatch(criterion.operator, raw, criterion.value);
   }

   if (field.type === 'number') {
      const a = toNumber(raw);
      const b = toNumber(criterion.value);
      const c = toNumber(criterion.valueTo);
      return compare(criterion.operator, a, b, c);
   }

   // date
   const a = toDateMs(raw);
   const b = toDateMs(criterion.value);
   const c = toDateMs(criterion.valueTo);
   return compare(criterion.operator, a, b, c);
}

export function applyReportFilters(
   allRows: ReportRowVm[],
   criteria: FilterCriterion[],
   fields: FilterFieldDef[]
): ReportRowVm[] {
   const active = (criteria ?? [])
      .filter((c) => c.fieldKey && c.operator)
      .map((c) => ({
         ...c,
         joinWithPrev: c.joinWithPrev ?? 'AND'
      }));

   if (!active.length) return allRows;

   const fieldMap = new Map(fields.map((f) => [f.key, f]));

   return allRows.filter((row) => {
      let result = true;

      for (let i = 0; i < active.length; i++) {
         const c = active[i];
         const f = fieldMap.get(c.fieldKey!);
         if (!f) continue;

         const ok = rowMatchesCriterion(row, f, c);

         if (i === 0) {
            result = ok;
         } else {
            result = c.joinWithPrev === 'OR' ? result || ok : result && ok;
         }
      }

      return result;
   });
}
